/**
 * LoanTypeSelector
 *
 * Three-tab loan-type preset — FHA (3.5% down) / Conventional (5%) /
 * Investor (20%). Tapping a tab sets the down-payment percentage and
 * clears the PMI on Investor (≥20% down has no PMI requirement).
 *
 * Shown at the top of the Affordability Calculator so the user can
 * jump between the most common loan archetypes in one tap before
 * fine-tuning the rest of the inputs by hand. The selector reads the
 * current down-payment percentage to highlight the matching preset;
 * a custom percentage outside the three known values leaves all tabs
 * un-highlighted, which is intentional — we don't want to lie about
 * which preset the user is on.
 */
import type { MortgageInputs } from '@/types/mortgage';

const LOAN_PRESETS = {
    FHA: { label: 'FHA', downPayment: 3.5, description: '3.5% down' },
    Conventional: { label: 'Conventional', downPayment: 5, description: '5% down' },
    Investor: { label: 'Investor', downPayment: 20, description: '20% down' },
} as const;

type LoanPresetKey = keyof typeof LOAN_PRESETS;

interface LoanTypeSelectorProps {
    downPaymentPercentage: number;
    onUpdateDownPaymentByPercentage: (percentage: number) => void;
    onUpdateInput: <K extends keyof MortgageInputs>(key: K, value: MortgageInputs[K]) => void;
}

export function LoanTypeSelector({
    downPaymentPercentage,
    onUpdateDownPaymentByPercentage,
    onUpdateInput,
}: LoanTypeSelectorProps) {
    const currentPreset = (Object.entries(LOAN_PRESETS).find(
        ([, preset]) => Math.abs(preset.downPayment - downPaymentPercentage) < 0.1,
    )?.[0] as LoanPresetKey | undefined);

    const handlePresetChange = (preset: LoanPresetKey) => {
        const { downPayment } = LOAN_PRESETS[preset];
        onUpdateDownPaymentByPercentage(downPayment);
        if (downPayment >= 20) {
            onUpdateInput('pmiPercentage', 0);
        }
    };

    return (
        <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
                Loan Type
            </label>
            <div className="grid grid-cols-3 gap-2">
                {(Object.keys(LOAN_PRESETS) as LoanPresetKey[]).map((key) => {
                    const preset = LOAN_PRESETS[key];
                    const isActive = currentPreset === key;
                    return (
                        <button
                            type="button"
                            key={key}
                            onClick={() => handlePresetChange(key)}
                            className={[
                                'rounded-lg px-3 py-2.5 text-sm font-medium transition-all',
                                isActive
                                    ? 'bg-primary text-white shadow-sm'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                            ].join(' ')}
                        >
                            <span className="block">{preset.label}</span>
                            <span
                                className={`block text-[11px] ${
                                    isActive ? 'text-white/80' : 'text-gray-500'
                                }`}
                            >
                                {preset.description}
                            </span>
                        </button>
                    );
                })}
            </div>
            {downPaymentPercentage >= 20 && (
                <p className="mt-2 text-xs text-green-600">
                    No PMI required with 20%+ down payment.
                </p>
            )}
        </div>
    );
}

export default LoanTypeSelector;
